import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { AnswerResponse, QuizState } from "@/types";
import { QuestionCard } from "@/components/quiz/QuestionCard";
import { Spinner } from "@/components/ui/Spinner";
import { Card } from "@/components/ui/Card";
import type { OptionState } from "@/components/quiz/OptionButton";

export default function TestRunner() {
  const { submissionId = "" } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<QuizState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Chiusa
  const [optionStates, setOptionStates] = useState<OptionState[]>([]);
  const [lastAnswer, setLastAnswer] = useState<AnswerResponse | null>(null);

  // Aperta
  const [testo, setTesto] = useState("");
  const [sendingAperta, setSendingAperta] = useState(false);

  // Anti double-complete
  const completingRef = useRef(false);

  const loadQuestion = useCallback(async (index: number) => {
    setLoading(true);
    setOptionStates([]);
    setLastAnswer(null);
    setTesto("");
    try {
      const s = await api.get<QuizState>(
        `/submissions/${submissionId}/question/${index}`,
      );
      setState(s);
    } catch (e: any) {
      const msg = e.message ?? "";
      if (msg.includes("ALREADY_COMPLETED") || msg.includes("già completato")) {
        navigate(`/area/risultati/${submissionId}`, { replace: true });
        return;
      }
      if (msg.includes("TIME_EXPIRED") || msg.includes("Tempo scaduto")) {
        await completeNow();
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, navigate]);

  useEffect(() => {
    loadQuestion(0);
  }, [loadQuestion]);

  async function completeNow() {
    if (completingRef.current) return;
    completingRef.current = true;
    try {
      await api.post(`/submissions/${submissionId}/complete`);
    } catch {/* tollera errori, il backend è idempotente */}
    sessionStorage.removeItem("its_quiz_state");
    navigate(`/area/risultati/${submissionId}`, { replace: true });
  }

  async function handleSelect(i: number) {
    if (!state) return;

    if (state.domandaCorrente.tipo === "chiusa") {
      // Tutti gli altri pulsanti → disabled, il cliccato → loading
      setOptionStates(
        state.domandaCorrente.opzioniShufflate?.map((_, idx) =>
          idx === i ? "loading" : "disabled",
        ) ?? [],
      );

      try {
        const r = await api.post<AnswerResponse>(`/answers/${submissionId}`, {
          questionId: state.domandaCorrente.id,
          rispostaIndice: i,
        });
        setLastAnswer(r);
        setOptionStates(
          state.domandaCorrente.opzioniShufflate?.map((_, idx) => {
            if (idx === i) return r.isCorrect ? "correct" : "wrong";
            return "disabled";
          }) ?? [],
        );
        // Aggiorna timer (lato server può essere cambiato)
        if (r.secondiRimasti !== undefined) {
          setState({ ...state, secondiRimasti: r.secondiRimasti });
        }
      } catch (e: any) {
        setError(e.message);
        setOptionStates([]);
      }
    } else if (state.domandaCorrente.tipo === "scala") {
      try {
        const r = await api.post<AnswerResponse>(`/answers/${submissionId}`, {
          questionId: state.domandaCorrente.id,
          rispostaScala: i + 1,
        });
        setLastAnswer(r);
        await goNext();
      } catch (e: any) { setError(e.message); }
    }
  }

  async function handleSubmitAperta() {
    if (!state) return;
    setSendingAperta(true);
    try {
      const r = await api.post<AnswerResponse>(`/answers/${submissionId}`, {
        questionId: state.domandaCorrente.id,
        rispostaTesto: testo.trim(),
      });
      setLastAnswer(r);
      await goNext();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSendingAperta(false);
    }
  }

  async function goNext() {
    if (!state) return;
    const next = state.prossimaIndex + 1;
    if (next >= state.totaleDomande) {
      await completeNow();
    } else {
      await loadQuestion(next);
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  }

  if (error || !state) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md text-center"><p className="text-red-300">{error}</p></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <QuestionCard
          domanda={state.domandaCorrente}
          index={state.prossimaIndex}
          totale={state.totaleDomande}
          secondiRimasti={state.secondiRimasti}
          onExpired={completeNow}
          optionStates={optionStates}
          onSelect={handleSelect}
          rispostaTesto={testo}
          onChangeTesto={setTesto}
          onSubmitAperta={handleSubmitAperta}
          sending={sendingAperta}
          isCorrect={lastAnswer?.isCorrect ?? null}
        />

        {state.domandaCorrente.tipo === "chiusa" && lastAnswer && (
          <div className="flex justify-end">
            <button
              onClick={goNext}
              className="bg-blue text-white px-5 py-3 rounded-xl font-mono text-sm hover:brightness-110"
            >
              {state.prossimaIndex + 1 >= state.totaleDomande
                ? "Concludi test →"
                : "Prossima domanda →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
